import React, {useState} from "react";
import {Container, LoginButton, GuidesList} from "../../styles/Styles";
import AdminNav from "./AdminNav";
import {useQuery} from "@apollo/client";
import {GET_GUIDES_LIST} from "../gql/guides-gql";
import {InfinitySpin} from "react-loader-spinner";
import GuideForm from "../form/AddGuideForm";
import { useAuth } from '../../context/AuthContext';
import { Roles } from '../common/roles';
import { Navigate } from 'react-router-dom';
import styled from "styled-components";
import {FaEdit} from "react-icons/fa";

const EditButton = styled.button`
    position: absolute;
    top: 10px;
    right: 10px;
    background: none;
    border: none;
    color: #1890ff;
    cursor: pointer;
    padding: 5px;
    display: none;
    align-items: center;
    gap: 5px;
    font-size: 0.9em;
    
    &:hover {
        color: #40a9ff;
    }
`;

const GuideCard = styled.div`
    position: relative;
    background: white;
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: transform 0.2s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
        
        ${EditButton} {
            display: flex;
        }
    }

    .guide-name {
        font-size: 1.2em;
        font-weight: bold;
        margin-bottom: 10px;
        color: #333;
    }

    .guide-languages {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 15px;
        padding: 10px;
        background: #f8f9fa;
        border-radius: 6px;

        span {
            background: #e6f7ff;
            color: #1890ff;
            padding: 4px 12px;
            border-radius: 4px;
            font-size: 0.9em;
            margin-right: 8px;
            margin-bottom: 8px;
            display: inline-flex;
            align-items: center;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
            
            &:last-child {
                margin-right: 0;
            }
        }
    }

    .guide-tours {
        color: #666;
        font-size: 0.9em;
        padding-top: 10px;
        border-top: 1px solid #eee;
    }
`;


const AdminDashboardGuides: React.FC = () => {
    const [showAddModal, setShowAddModal] = useState(false);
    const [selectedGuide, setSelectedGuide] = useState<any>(null);
    const {data, loading, error} = useQuery(GET_GUIDES_LIST);
    const { user, isAuthenticated } = useAuth();

    if (loading) return <InfinitySpin />;
    if (error) return <div>Error: {error.message}</div>;

    const guides = data?.getGuidesList || [];

    const handleEditClick = (guide: any) => {
        setSelectedGuide(guide);
        setShowAddModal(true);
    };

    const handleCloseModal = () => {
        setShowAddModal(false);
        setSelectedGuide(null);
    };

    if (!isAuthenticated || user?.role[0] !== Roles.ADMIN) {
        return <Navigate to="/" />;
    }

    return (
        <Container>
            <AdminNav />
            <h2>Guides list</h2>
            <LoginButton onClick={() => { setSelectedGuide(null); setShowAddModal(true); }}>Add guide</LoginButton>
            <GuidesList>
                {guides.length === 0
                    ? 'No guides yet'
                    : guides?.map((guide: any) => (
                        <GuideCard key={guide._id}>
                            <EditButton onClick={() => handleEditClick(guide)}>
                                <FaEdit /> Edit
                            </EditButton>
                            <div className="guide-name">
                                {guide?.profile?.firstName} {guide?.profile?.lastName}
                            </div>
                            <div className="guide-languages">
                                {guide?.profile?.languages?.map((lang: string, index: number) => (
                                    <span key={index}>{lang}</span>
                                ))}
                            </div>
                            <div className="guide-tours">
                                {guide?.tourLabel?.title ? (
                                    <div>
                                        <strong>Assigned Tour:</strong> {guide.tourLabel.title}
                                        {guide.tourLabel.price && <span> (€{guide.tourLabel.price})</span>}
                                    </div>
                                ) : (
                                    <div>No assigned tours</div>
                                )}
                            </div>
                        </GuideCard>
                    ))}
            </GuidesList>
            {showAddModal && (
                <GuideForm
                    key={selectedGuide?._id ?? 'new'}
                    guideToEdit={selectedGuide}
                    onClose={handleCloseModal}
                />
            )}
        </Container>
    );
}

export default AdminDashboardGuides;
